import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import {
  C,
  T,
  Masks,
  Sum,
  roundToDecimals,
  useRespostaByPerguntaCode,
  averagePriceImpactosQualidade as avgPrice,
} from 'shared/helpers';
import { useSelector, useDispatch } from 'react-redux';

import Styles from './ImpactosDeQualidade.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faMedal } from '@fortawesome/free-solid-svg-icons';

import Header from 'components/Header/Header';
import Table from './NewTable/Table';
import Editor from './Editor/Editor';

import { Creators as reduxImpactos } from 'store/ducks/impactos';
import { Creators as reduxPerguntas } from 'store/ducks/perguntas';
import { Creators as reduxRespostas } from 'store/ducks/respostas';
import { Creators as reduxEtapas } from 'store/ducks/etapas';
import { Creators as reduxProdutos } from 'store/ducks/produtos';

import { useEditableContext } from 'shared/hooks';
import PrintButton from 'components/PrintButton/PrintButton';
import WithPermission from 'components/Template/WithPermission/WithPermission';

const baseCategories = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'Descarte'];

export default function ImpactosDeQualidade({ match }) {
  const { ImpactosDeQualidade, Selected } = Styles;
  const { id } = match.params;
  const dispatch = useDispatch();

  const Perguntas = useSelector((state) => state.perguntas.data || []);
  const Impactos = useSelector((state) => state.impactos.data);
  const Respostas = useSelector((state) => state.respostas.data || []);
  const REtapas = useSelector((state) => state.etapas.data);
  const PEM = useSelector((state) => state.impactos.pem);

  const PExpectativas = Perguntas.filter((x) => x.fase_id === 8);
  const Savings = Perguntas.filter((x) => x.fase_id === 7);

  const [edit, setEdit] = useState(false);
  const [init, setInit] = useState(false);
  const auth = useSelector((state) => state.user.data);
  const produtividadeConvencional =
    useRespostaByPerguntaCode(Perguntas, Respostas, 'produtividadeHa') || 0;

  useEditableContext(edit);

  useEffect(() => {
    if (id) {
      dispatch(reduxImpactos.getImpactos(id));
      dispatch(reduxEtapas.getEtapas(id));
      dispatch(reduxProdutos.getProdutos());
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (!init) {
      dispatch(
        reduxPerguntas.getPerguntas((x) => x.fase_id === 7 || x.fase_id === 8),
      );
      setInit(true);
    }
  }, [id, init, dispatch]);

  useEffect(() => {
    dispatch(
      reduxRespostas.getRespostas((x) => String(x.pem_id) === String(id)),
    );
  }, [id, dispatch, Perguntas]);

  const Etapas = REtapas.map((x) => ({
    ...x,
    produtosSQMV: x.produtos.filter((y) => y.fase_id === 2),
    produtosCOND: x.produtos.filter((y) => y.fase_id === 3),
  }));

  const Totals = Etapas.map((etapa) => {
    let totalSQMV = 0;
    let totalCOND = 0;
    for (let i = 0; i < etapa.produtosSQMV.length; i++) {
      totalSQMV += Number(
        (etapa.produtosSQMV[i].valor / 1000) * etapa.produtosSQMV[i].quantidade,
      );
    }
    for (let i = 0; i < etapa.produtosCOND.length; i++) {
      totalCOND += Number(
        (etapa.produtosCOND[i].valor / 1000) * etapa.produtosCOND[i].quantidade,
      );
    }

    return [totalSQMV, totalCOND];
  });

  const E = useCallback(
    (i) =>
      T(
        T(Respostas, []).find(
          (x) =>
            String(x.pergunta_id) === String(T(T(PExpectativas, []).find(p => p.code === i)).id),
        ),
      ).resposta || 0,
    [Respostas, PExpectativas],
  );

  const totalSavings = Sum(
    Respostas.filter((x) =>
      Savings.map((y) => y.id).includes(x.pergunta_id),
    ).map((z) => T(z).resposta || 0),
  );

  const custoFertilizacaoSQM = Sum(Totals.map((x) => x[0])) - totalSavings;
  const custoFertilizacaoConv = Sum(Totals.map((x) => x[1])) - 0;
  const diffCustoFertilicao = custoFertilizacaoSQM - custoFertilizacaoConv;

  const data = useMemo(() => {
    const { number, currency } = Masks;

    const precoMedio = (produtividade, recomendacao) =>
      roundToDecimals(
        Sum(
          T(T(T(Impactos).preco, [])[recomendacao], []).map(
            (preco, i) =>
              preco *
              produtividade *
              (Impactos.porcentagem[recomendacao][i] / 100),
          ),
        ) / (produtividade || 0),
      );
    const avgPriceQuality = [
      precoMedio(T(Impactos).produtividade || 0, 'svb'),
      precoMedio(produtividadeConvencional, 'convencional'),
      precoMedio(T(Impactos).produtividade || 0, 'svb') -
        precoMedio(produtividadeConvencional, 'convencional'),
      (precoMedio(T(Impactos).produtividade || 0, 'svb') /
        precoMedio(produtividadeConvencional, 'convencional')) *
        100 -
        100,
      (precoMedio(T(Impactos).produtividade || 0, 'svb') -
        precoMedio(produtividadeConvencional, 'convencional')) *
        E('areaTotal') *
        E('produtividadeHa'),
    ];
    return [
      [
        number(T(Impactos).produtividade || 0, { ed: 2 }),
        number(produtividadeConvencional, { ed: 2 }),
        number(T(Impactos).produtividade - produtividadeConvencional, {
          ed: 2,
          suffix: ` ${E('unidadeProducao') || 'kg'}/ha`,
        }),
        number(
          ((T(Impactos).produtividade - produtividadeConvencional) /
            produtividadeConvencional) *
            100,
          {
            ed: 2,
            suffix: '%',
          },
        ),
        number(
          (T(Impactos).produtividade - produtividadeConvencional) *
            Number(E('areaTotal')),
          {
            ed: 2,
            suffix: E('unidadeProducao') || 'kg',
          },
        ),
        true,
      ],
      [
        currency(custoFertilizacaoSQM, { suffix: ' /ha' }),
        currency(custoFertilizacaoConv, { suffix: ' /ha' }),
        currency(diffCustoFertilicao, {
          suffix: ' /ha',
        }),
        number((diffCustoFertilicao / custoFertilizacaoConv) * 100, {
          suffix: '%',
        }),
        number(diffCustoFertilicao * Number(E('areaTotal')), {
          prefix: 'R$ ',
        }),
        true,
      ],
      ...Object.values(T(T(Impactos).classificacao))
        .map((_, y) => {
          const x = T(T(T(Impactos).porcentagem).svb, [])[y];
          return [
            number(x, {
              suffix: '%',
            }),
            number(Impactos.porcentagem.convencional[y], {
              suffix: '%',
            }),
            number(x - Impactos.porcentagem.convencional[y], {
              suffix: '%',
            }),
            number((x / Impactos.porcentagem.convencional[y]) * 100 - 100, {
              suffix: '%',
            }),
            'xx',
            !(
              !Number(Impactos.porcentagem.convencional[y]) &&
              !Number(Impactos.porcentagem.svb[y])
            ),
          ];
        })
        .filter((z) => z !== undefined),
      ...Object.values(T(T(Impactos).classificacao))
        .filter((v, _, original) => v !== original.slice(-1)[0])
        .map((_, y) => {
          const x = T(T(T(Impactos).preco).svb, [])[y];
          return [
            number(x, { prefix: 'R$ ' }),
            number(Impactos.preco.convencional[y], { prefix: 'R$ ' }),
            number(x - Impactos.preco.convencional[y], { prefix: 'R$ ' }),
            number((x / Impactos.preco.convencional[y]) * 100 - 100, {
              suffix: '%',
            }),
            number((x - Impactos.preco.convencional[y]) * Number(E('areaTotal')), {
              prefix: 'R$ ',
            }),
            !(
              !Number(Impactos.porcentagem.convencional[y]) &&
              !Number(Impactos.porcentagem.svb[y])
            ),
          ];
        })
        .filter((z) => z !== undefined),

      [
        number(avgPriceQuality[0], {
          prefix: 'R$ ',
        }),
        number(avgPriceQuality[1], { prefix: 'R$ ' }),
        number(avgPriceQuality[2], {
          prefix: 'R$ ',
          ed: 2,
          suffix: ` /${E('unidadeProducao') || 'kg'}`,
        }),
        number(avgPriceQuality[3], { suffix: ' %' }),
        number(avgPriceQuality[4], { prefix: 'R$ ' }),
        true,
      ],
      [
        currency(avgPrice(Impactos, 'svb', produtividadeConvencional), {
          suffix: ' /ha',
        }),
        currency(
          avgPrice(Impactos, 'convencional', produtividadeConvencional),
          {
            suffix: ' /ha',
          },
        ),
        currency(
          avgPrice(Impactos, 'svb', produtividadeConvencional) -
            avgPrice(Impactos, 'convencional', produtividadeConvencional),
          {
            suffix: ' /ha',
          },
        ),
        number(
          (avgPrice(Impactos, 'svb', produtividadeConvencional) /
            avgPrice(Impactos, 'convencional', produtividadeConvencional) ||
            1) *
            100 -
            100,
          { suffix: ' %' },
        ),
        currency(
          (avgPrice(Impactos, 'svb', produtividadeConvencional) -
            avgPrice(Impactos, 'convencional', produtividadeConvencional)) *
            E('areaTotal'),
        ),
        true,
      ],
      [
        currency(
          avgPrice(Impactos, 'svb', produtividadeConvencional) -
            custoFertilizacaoSQM,
          {
            suffix: ' /ha',
          },
        ),
        currency(
          avgPrice(Impactos, 'convencional', produtividadeConvencional) -
            custoFertilizacaoConv,
          {
            suffix: ' /ha',
          },
        ),
        currency(
          avgPrice(Impactos, 'svb', produtividadeConvencional) -
            custoFertilizacaoSQM -
            (avgPrice(Impactos, 'convencional', produtividadeConvencional) -
              custoFertilizacaoConv),
          { suffix: ' /ha' },
        ),
        number(
          ((avgPrice(Impactos, 'svb', produtividadeConvencional) -
            custoFertilizacaoSQM) /
            (avgPrice(Impactos, 'convencional', produtividadeConvencional) -
              custoFertilizacaoConv)) *
            100 -
            100,
          { suffix: ' %' },
        ),
        currency(
          (avgPrice(Impactos, 'svb', produtividadeConvencional) -
            custoFertilizacaoSQM -
            (avgPrice(Impactos, 'convencional', produtividadeConvencional) -
              custoFertilizacaoConv)) *
            Number(E('areaTotal')),
        ),
        true,
      ],
    ];
  }, [
    Impactos,
    E,
    custoFertilizacaoConv,
    custoFertilizacaoSQM,
    diffCustoFertilicao,
    produtividadeConvencional,
  ]);

  const chartRef = useRef();

  if (PEM !== id) {
    return null;
  }

  const submit = (form) => {
    dispatch(reduxImpactos.updateImpactos(id, form));
    setEdit(!edit);
  };

  return (
    <div className={ImpactosDeQualidade}>
      <Header title={'IMPACTOS DE QUALIDADE'} icon={faMedal}>
        <WithPermission permission='impactos.update'>
          <button
            id='editar'
            className={C([edit, Selected])}
            onClick={() => setEdit(!edit)}
          >
            <FontAwesomeIcon icon={faEdit} />
          </button>
        </WithPermission>
        <PrintButton fitElements={[chartRef]} />
      </Header>
      <Table
        data={data}
        impactos={Impactos}
        baseCategories={baseCategories}
        chartRef={chartRef}
        productionUnit={E('unidadeProducao') || 'kg'}
      />
      <Editor
        id={id}
        etec={auth.id}
        close={() => setEdit(false)}
        open={edit}
        submit={submit}
        produtividade={produtividadeConvencional}
        baseCategories={baseCategories}
      />
    </div>
  );
}

ImpactosDeQualidade.propTypes = {
  match: PropTypes.any.isRequired,
};
